import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Spin, Alert } from 'antd'
import { ExclamationCircleOutlined, TeamOutlined } from '@ant-design/icons'
import { useSupervisaoVendas } from '../hooks/useSupervisaoVendas'
import { SupervisaoHeader } from '../components/supervisao/SupervisaoHeader'
import { RelacionadoresTable } from '../components/supervisao/RelacionadoresTable'
import { RelacionadorDetalhesModal } from '../components/supervisao/RelacionadorDetalhesModal'
import { ContratosSemVinculoModal } from '../components/supervisao/ContratosSemVinculoModal'
import { BaseDashboard } from '@/features/dashboard/components/role-dashboards/BaseDashboard'
import { useProductionPeriodFilter } from '@/hooks/useProductionPeriodFilter'
import { useProductionStartDay } from '@/hooks/useProductionStartDay'
import { getRecentProductionPeriods } from '@/utils/productionPeriod'
import type { RelacionadorMetricas } from '../services/supervisaoVendas.service'

export function SupervisaoVendasPage() {
  const queryClient = useQueryClient()
  const { productionPeriodFilter, setProductionPeriodFilter } = useProductionPeriodFilter()
  const startDay = useProductionStartDay()
  const recentPeriods = getRecentProductionPeriods(12, startDay)

  const mes = productionPeriodFilter?.month ?? new Date().getMonth() + 1
  const ano = productionPeriodFilter?.year ?? new Date().getFullYear()

  const [selectedRelacionador, setSelectedRelacionador] = useState<RelacionadorMetricas | null>(null)
  const [isSemVinculoModalOpen, setIsSemVinculoModalOpen] = useState(false)

  const { data, isLoading, error } = useSupervisaoVendas({ mes, ano })

  const productionPeriodValue = productionPeriodFilter
    ? `${productionPeriodFilter.month}-${productionPeriodFilter.year}`
    : `${mes}-${ano}`

  const periodOptions = useMemo(
    () =>
      recentPeriods.map((period) => ({
        label: period.label.charAt(0).toUpperCase() + period.label.slice(1),
        value: `${period.month}-${period.year}`,
      })),
    [recentPeriods]
  )

  const handlePeriodChange = (value: string) => {
    if (!value) {
      setProductionPeriodFilter(null)
      return
    }
    const [month, year] = value.split('-').map(Number)
    setProductionPeriodFilter({ month, year })
  }

  return (
    <BaseDashboard
      title="Supervisão de Vendas"
      productionPeriod={productionPeriodValue}
      onProductionPeriodChange={handlePeriodChange}
      productionPeriods={periodOptions}
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          message="Erro ao carregar dados"
          description={error instanceof Error ? error.message : 'Erro desconhecido'}
        />
      ) : isLoading || !data ? (
        <div className="flex items-center justify-center py-24">
          <Spin size="large" />
        </div>
      ) : (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
              Acompanhamento de desempenho
            </p>
            <p className="text-sm text-[#475569]">
              Supervisor: <span className="font-semibold text-[#0f172a]">{data.supervisor.nome}</span>
            </p>
          </div>

          <SupervisaoHeader resumo={data.resumo} />

          {data.resumo.produzidoTotal === 0 && (
            <div className="rounded-[20px] border border-red-200/80 bg-red-50/70 dark:border-red-500/30 dark:bg-red-500/10 p-4 flex items-start gap-3">
              <ExclamationCircleOutlined className="text-red-600 dark:text-red-400 mt-0.5 text-lg" />
              <div>
                <h3 className="font-semibold text-red-900 dark:text-red-300">
                  Nenhum contrato encontrado no período
                </h3>
                <p className="text-sm text-red-700 mt-1 dark:text-red-400/90">
                  Há {data.resumo.reunioesTotal} agendamentos, mas 0 contratos em {data.periodo.mes}/
                  {data.periodo.ano}.
                </p>
                <ul className="text-sm text-red-700 dark:text-red-400/90 mt-2 ml-4 list-disc space-y-0.5">
                  <li>Nenhum contrato foi criado neste período</li>
                  <li>Contratos criados em período diferente</li>
                  <li>Cliente do agendamento diferente do cliente do contrato</li>
                  <li>Contratos sem relacionador vinculado</li>
                </ul>
              </div>
            </div>
          )}

          {data.resumo.contratosAguardandoVinculo > 0 && (
            <div className="rounded-[20px] border border-amber-200/80 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-500/10 p-4 flex items-start gap-3">
              <ExclamationCircleOutlined className="text-amber-600 dark:text-amber-400 mt-0.5 text-lg" />
              <div className="flex-1 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-amber-900 dark:text-amber-300">
                    {data.resumo.contratosAguardandoVinculo} contrato
                    {data.resumo.contratosAguardandoVinculo === 1 ? '' : 's'} aguardando vínculo
                  </h3>
                  <p className="text-sm text-amber-800 mt-1 dark:text-amber-400/90">
                    Gerencie os relacionadores desses contratos para completar o vínculo.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsSemVinculoModalOpen(true)}
                  className="text-sm font-semibold text-amber-900 underline hover:text-amber-700 dark:text-amber-300 dark:hover:text-amber-200 whitespace-nowrap"
                >
                  Visualizar contratos
                </button>
              </div>
            </div>
          )}

          <BaseDashboard.ChartCard
            title="Equipe de Vendas"
            icon={<TeamOutlined style={{ color: '#FF3737' }} />}
          >
            <p className="text-sm text-[#64748b] mb-4 -mt-2">
              {data.relacionadores.length} relacionador
              {data.relacionadores.length === 1 ? '' : 'es'} na equipe
            </p>
            <RelacionadoresTable
              relacionadores={data.relacionadores}
              onRowClick={setSelectedRelacionador}
              isLoading={isLoading}
            />
          </BaseDashboard.ChartCard>

          {selectedRelacionador && (
            <RelacionadorDetalhesModal
              relacionador={selectedRelacionador}
              periodo={data.periodo}
              vinculos={data.vinculos}
              onClose={() => setSelectedRelacionador(null)}
            />
          )}

          {isSemVinculoModalOpen && (
            <ContratosSemVinculoModal
              isOpen={isSemVinculoModalOpen}
              onClose={() => setIsSemVinculoModalOpen(false)}
              vinculos={data.vinculos}
              relacionadores={data.relacionadores.map((r) => ({
                relacionadorId: r.relacionadorId,
                nome: r.nome,
              }))}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ['supervisao-vendas'] })
              }}
            />
          )}
        </div>
      )}
    </BaseDashboard>
  )
}
